// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DataCache } from '@shared/commons/DataCache';
import { PositionBalanceID, PositionsBalance } from '@shared/commons/cache/PositionsBalance';
import { EventEmitter } from 'events';
export var PositionNotifyType;
(function (PositionNotifyType) {
    PositionNotifyType[PositionNotifyType["Add"] = 0] = "Add";
    PositionNotifyType[PositionNotifyType["Remove"] = 1] = "Remove";
    PositionNotifyType[PositionNotifyType["Update"] = 2] = "Update";
})(PositionNotifyType || (PositionNotifyType = {}));
export class PositionsBalanceManager {
    constructor() {
        this._positionsBalanceDict = {};
        this._accountIDs = [];
        this._eventEmitter = new EventEmitter();
    }
    subscribeToAdd(callback) {
        this._eventEmitter.on('add', callback);
    }
    subscribeToRemove(callback) {
        this._eventEmitter.on('remove', callback);
    }
    subscribeToUpdate(callback) {
        this._eventEmitter.on('update', callback);
    }
    unsubscribeToAdd(callback) {
        this._eventEmitter.off('add', callback);
    }
    unsubscribeToRemove(callback) {
        this._eventEmitter.off('remove', callback);
    }
    unsubscribeToUpdate(callback) {
        this._eventEmitter.off('update', callback);
    }
    filter(accountIDs) {
        this._accountIDs = accountIDs.filter((id) => !isNullOrUndefined(id));
    }
    run() {
        this._positionsBalanceDict = {};
        this.populate();
        this.subscribe();
    }
    stop() {
        this.unsubscribe();
    }
    populate() {
        const positions = DataCache.Trading.getAllPositions();
        for (const pos of positions) {
            this.addPositionEvent(pos);
        }
        const positionsCorporateAction = DataCache.Trading.getAllPositionsCorporateAction();
        for (const pos of positionsCorporateAction) {
            this.addPositionEvent(pos);
        }
    }
    subscribe() {
        DataCache.OnAddPosition.Subscribe(this.addPositionEvent, this);
        DataCache.OnUpdatePosition.Subscribe(this.updatePositionEvent, this);
        DataCache.OnRemovePosition.Subscribe(this.removePositionEvent, this);
        // CorporateAction
        DataCache.OnAddCorporateAction.Subscribe(this.addPositionEvent, this);
        DataCache.OnUpdateCorporateAction.Subscribe(this.updatePositionEvent, this);
        DataCache.OnRemoveCorporateAction.Subscribe(this.removePositionEvent, this);
    }
    unsubscribe() {
        DataCache.OnAddPosition.UnSubscribe(this.addPositionEvent, this);
        DataCache.OnUpdatePosition.UnSubscribe(this.updatePositionEvent, this);
        DataCache.OnRemovePosition.UnSubscribe(this.removePositionEvent, this);
        // CorporateAction
        DataCache.OnAddCorporateAction.UnSubscribe(this.addPositionEvent, this);
        DataCache.OnUpdateCorporateAction.UnSubscribe(this.updatePositionEvent, this);
        DataCache.OnRemoveCorporateAction.UnSubscribe(this.removePositionEvent, this);
    }
    // #region Methods
    addPositionsBalance(positionsBalance) {
        this._eventEmitter.emit('add', positionsBalance);
    }
    removePositionsBalance(positionsBalance) {
        this._eventEmitter.emit('remove', positionsBalance);
    }
    updatePositionsBalance(positionsBalance) {
        this._eventEmitter.emit('update', positionsBalance);
    }
    // #endregion
    // #region Events
    addPositionEvent(position) {
        this.updatePositionBalance(position, PositionNotifyType.Add);
    }
    removePositionEvent(position) {
        this.updatePositionBalance(position, PositionNotifyType.Remove);
    }
    updatePositionEvent(position) {
        this.updatePositionBalance(position, PositionNotifyType.Update);
    }
    // #endregion
    updatePositionBalance(position, state) {
        var _a;
        if (this._accountIDs.length > 0 && !this._accountIDs.includes(position.Account.AcctNumber)) {
            return;
        }
        const pbID = new PositionBalanceID((_a = position.Instrument) === null || _a === void 0 ? void 0 : _a.InstrumentTradableID, position.ProductType);
        let positionBalance = this._positionsBalanceDict[pbID.toString()];
        switch (state) {
            case PositionNotifyType.Add:
                if (isNullOrUndefined(positionBalance)) {
                    positionBalance = new PositionsBalance();
                    positionBalance.addPosition(position);
                    this._positionsBalanceDict[pbID.toString()] = positionBalance;
                    this.addPositionsBalance(positionBalance);
                }
                else {
                    positionBalance.addPosition(position);
                    this.updatePositionsBalance(positionBalance);
                }
                break;
            case PositionNotifyType.Remove:
                if (!isNullOrUndefined(positionBalance)) {
                    positionBalance.removePosition(position);
                    if (Object.values(positionBalance.Positions).length === 0) {
                        this.removePositionsBalance(positionBalance);
                        delete this._positionsBalanceDict[pbID.toString()];
                    }
                    else {
                        this.updatePositionsBalance(positionBalance);
                    }
                }
                break;
            case PositionNotifyType.Update:
                this.updatePositionsBalance(positionBalance);
                break;
        }
    }
}
